import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Calendar, Search, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { SEO } from '../components/SEO';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BlogPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const params = {};
        if (selectedCategory !== 'Tous') {
          params.category = selectedCategory;
        }
        if (searchTerm) {
          params.search = searchTerm;
        }
        
        const response = await axios.get(`${API}/articles`, { params });
        setArticles(response.data);
      } catch (error) {
        console.error('Erreur lors du chargement des articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [selectedCategory, searchTerm]);

  const categories = ['Tous', ...new Set(articles.map(article => article.category))];

  const filteredArticles = articles;

  return (
    <div className="min-h-screen bg-cream">
      <SEO 
        title="Blog Apiculture Urbaine | Guides, Conseils & Actualités - UrbanHive"
        description="Découvrez nos articles sur l'apiculture urbaine : guides pratiques, conseils d'entretien, réglementation, écologie et récolte du miel. Articles par des experts en apiculture de balcon."
        keywords="blog apiculture, articles ruche urbaine, conseils apiculture, guide entretien ruche, actualités abeilles, réglementation apiculture"
        url="/blog"
        type="blog"
      />
      <Header />
      
      <main>
        {/* Page Header with Background Image */}
        <section className="relative py-24 overflow-hidden">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1587468362560-e64c0ede4c5a" 
              alt="Blog apiculture urbaine"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
          </div>
          
          {/* Content */}
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-4">
                Blog Apiculture Urbaine
              </h1>
              <p className="text-lg text-white/90">
                Guides, conseils et retours d'expérience pour réussir votre projet d'apiculture urbaine.
              </p>
            </div>
          </div>
        </section>

        {/* Search and Filters */}
        <section className="py-8 bg-white border-b">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              {/* Search Bar */}
              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <Input 
                  type="text"
                  placeholder="Rechercher des articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 py-6 text-lg border-2 focus:border-honey"
                />
              </div>

              {/* Category Filters */}
              <div className="flex flex-wrap gap-3">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full font-medium transition-all ${
                      selectedCategory === category
                        ? 'bg-honey text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              {filteredArticles.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/blog/${article.slug || article.id}`}
                      className="block"
                    >
                      <Card 
                        className="group hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden border-2 hover:border-honey h-full"
                      >
                        {/* Article Image */}
                        <div className="relative h-56 overflow-hidden">
                          <img 
                            src={article.image} 
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                          <div className="absolute top-3 left-3">
                            <Badge className="bg-honey text-white">{article.category}</Badge>
                          </div>
                        </div>

                        <CardContent className="p-6">
                          <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-honey transition-colors line-clamp-2">
                            {article.title}
                          </h3>
                          <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                            {article.excerpt}
                          </p>
                          
                          {/* Meta Info */}
                          <div className="flex items-center text-xs text-gray-500 mb-4">
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-3 w-3" />
                              <span>{new Date(article.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            </div>
                          </div>

                          <div className="flex items-center text-honey font-medium text-sm group-hover:underline">
                            Lire l'article complet
                            <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-xl text-gray-600">Aucun article trouvé pour votre recherche.</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};
